import { Input } from '@components/ui';
import CurrentJob from '@features/buyer/jobs/current-job/current-job';
import PastJob from '@features/buyer/jobs/past-job/past-job';
import { useDebouncedValue } from '@features/buyer/jobs/useDebouncedValue';
import {
  allyFilterLabelKey,
  allyFilters,
  defaultJobListFilter,
  type JobListFilter,
  jobListFilterIsActive,
  jobScopeLabelKey,
  jobScopes,
  retailFilterLabelKey,
  retailFiltersForConfig,
  sanitizeJobListFilter,
} from '@features/jobs';
import {
  FEATURE_FLAG_KEYS,
  useFeatureFlag,
} from '@lib/featureFlags';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type BuyerJobTab = 'current' | 'past';

const SEARCH_DEBOUNCE_MS = 300;

const pillSx = (selected: boolean) => ({
  flex: '0 0 auto',
  border: 0,
  cursor: 'pointer',
  borderRadius: 999,
  px: 1.5,
  py: 0.75,
  font: 'inherit',
  fontWeight: 600,
  fontSize: 13,
  fontVariantNumeric: 'tabular-nums',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  color: selected ? 'text.primary' : 'text.secondary',
  bgcolor: selected ? 'background.paper' : 'transparent',
  boxShadow: selected ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
});

const pillTrackSx = {
  gap: 0.5,
  p: 0.5,
  borderRadius: 999,
  bgcolor: 'action.hover',
};

const segmentedSx = {
  ...pillTrackSx,
  display: 'flex',
  flexWrap: 'wrap',
  width: 'max-content',
  maxWidth: '100%',
  boxSizing: 'border-box' as const,
};

const BuyerJobs = (): JSX.Element => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<BuyerJobTab>('current');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<JobListFilter>(defaultJobListFilter);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS).trim();
  const nonAutomotiveEnabled = useFeatureFlag(
    FEATURE_FLAG_KEYS.NON_AUTOMOTIVE_CATEGORY
  );
  const activeFilter = sanitizeJobListFilter(filter, nonAutomotiveEnabled);
  const visibleRetailFilters = retailFiltersForConfig(nonAutomotiveEnabled);
  const subtype =
    activeFilter.scope === 'ALLY'
      ? allyFilters
      : activeFilter.scope === 'RETAIL'
        ? visibleRetailFilters
        : [];

  useEffect(() => {
    if (filter.retail === 'NON_AUTOMOTIVE' && !nonAutomotiveEnabled) {
      setFilter(sanitizeJobListFilter(filter, false));
    }
  }, [filter, nonAutomotiveEnabled]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Tabs
          value={tab}
          onChange={(_event, value: BuyerJobTab) => setTab(value)}
          sx={{
            minHeight: 48,
            flexShrink: 0,
            '& .MuiTab-root': {
              minHeight: 48,
              px: 2.5,
              fontSize: 16,
              fontWeight: 600,
              textTransform: 'none',
              color: 'text.secondary',
            },
            '& .Mui-selected': { color: 'text.primary' },
          }}
        >
          <Tab value="current" label={t('jobs.current')} />
          <Tab value="past" label={t('jobs.past')} />
        </Tabs>
        <Box role="search" sx={{ flex: 1, minWidth: 0 }}>
          <Input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setSearch('');
            }}
            placeholder={t('jobs.searchPlaceholder')}
            inputProps={{
              'aria-label': t('jobs.search'),
              enterKeyHint: 'search',
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon
                    sx={{ fontSize: 20, color: 'text.secondary' }}
                  />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    aria-label={t('jobs.clearSearch')}
                    onClick={() => setSearch('')}
                  >
                    <CloseRoundedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        <Box sx={segmentedSx} role="group" aria-label={t('jobs.filterLabel')}>
          {jobScopes.map((scope) => {
            const selected = activeFilter.scope === scope;
            return (
              <Box
                key={scope}
                component="button"
                type="button"
                title={t(jobScopeLabelKey(scope))}
                aria-pressed={selected}
                onClick={() =>
                  setFilter({ ...defaultJobListFilter, scope })
                }
                sx={pillSx(selected)}
              >
                {t(jobScopeLabelKey(scope))}
              </Box>
            );
          })}
        </Box>
      </Box>

      {subtype.length > 0 ? (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={segmentedSx}>
            {activeFilter.scope === 'ALLY'
              ? allyFilters.map((ally) => {
                  const selected = activeFilter.ally === ally;
                  return (
                    <Box
                      key={ally}
                      component="button"
                      type="button"
                      title={t(allyFilterLabelKey(ally))}
                      aria-pressed={selected}
                      onClick={() => setFilter({ ...activeFilter, ally })}
                      sx={pillSx(selected)}
                    >
                      {t(allyFilterLabelKey(ally))}
                    </Box>
                  );
                })
              : visibleRetailFilters.map((retail) => {
                  const selected = activeFilter.retail === retail;
                  return (
                    <Box
                      key={retail}
                      component="button"
                      type="button"
                      title={t(retailFilterLabelKey(retail))}
                      aria-pressed={selected}
                      onClick={() => setFilter({ ...activeFilter, retail })}
                      sx={pillSx(selected)}
                    >
                      {t(retailFilterLabelKey(retail))}
                    </Box>
                  );
                })}
          </Box>
        </Box>
      ) : null}

      {tab === 'current' ? (
        <CurrentJob
          search={debouncedSearch}
          filter={activeFilter}
          filtering={jobListFilterIsActive(activeFilter)}
        />
      ) : (
        <PastJob
          search={debouncedSearch}
          filter={activeFilter}
          filtering={jobListFilterIsActive(activeFilter)}
        />
      )}
    </Box>
  );
};

export default BuyerJobs;
